import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  CardHeader,
  CardFooter,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const ResolutionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertType, setAlertType] = useState("success"); // success, error, warning

  useEffect(() => {
    const fetchMarketDetails = async () => {
      try {
        setLoading(true);
        const response = await fetchData(
          `api/event/resolution/market-details/${id}`,
        );

        if (response.success) {
          setMarketData(response);
        } else {
          console.error("Failed to fetch market details:", response);
        }
      } catch (error) {
        console.error("Error fetching market details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMarketDetails();
    }
  }, [id]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const formatDateToLocal = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const getStatusBadgeColor = (status) => {
    if (status?.includes("dispute")) {
      return "warning";
    } else if (status === "resolution_proposed") {
      return "info";
    } else if (status === "resolution_approved" || status === "finalized") {
      return "success";
    } else if (status === "approved") {
      return "success";
    } else if (status === "pending") {
      return "warning";
    } else if (status === "rejected") {
      return "danger";
    } else {
      return "secondary";
    }
  };

  // Calculate time remaining in resolution window
  const getDisputeWindowRemaining = () => {
    if (!marketData?.resolution_window_end) {
      return null;
    }

    const disputeWindowEnd = new Date(marketData.resolution_window_end);
    const now = new Date();

    // If dispute window has passed
    if (now > disputeWindowEnd) {
      return { expired: true, text: "Dispute window expired" };
    }

    const remainingMs = disputeWindowEnd - now;
    const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
    const remainingMinutes = Math.floor(
      (remainingMs % (1000 * 60 * 60)) / (1000 * 60),
    );

    return {
      expired: false,
      text: `Dispute window: ${remainingHours}h ${remainingMinutes}m remaining`,
      hours: remainingHours,
      minutes: remainingMinutes,
    };
  };

  // Handle the review submission
  const handleReviewSubmit = async (resolutionId, decision, notes) => {
    try {
      setActionLoading(true);

      const reviewData = {
        resolution_id: resolutionId,
        decision: decision,
        review_notes: notes || "",
      };

      const response = await postData(
        "api/event/resolution/review",
        reviewData,
      );

      if (response.success) {
        setAlertType("success");
        setAlertTitle("Success!");
        setAlertMessage(`Resolution ${decision} successfully!`);
        setModalOpen(false);
        setAlertModal(true);
        // We'll reload after alert is closed
      } else {
        setAlertType("danger");
        setAlertTitle("Error");
        setAlertMessage(`Failed to ${decision} resolution. Please try again.`);
        setModalOpen(false);
        setAlertModal(true);
      }
    } catch (error) {
      console.error(`Error ${decision} resolution:`, error);
      setAlertType("danger");
      setAlertTitle("Error");
      setAlertMessage(
        `An error occurred while ${
          decision === "approved" ? "approving" : "rejecting"
        } resolution.`,
      );
      setModalOpen(false);
      setAlertModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading market details...</p>
      </div>
    );
  }

  if (!marketData || !marketData.market) {
    return (
      <div className="text-center mt-5">
        <div className="alert alert-danger">
          <h4 className="alert-heading">Market Not Found</h4>
          <p>The requested market could not be found or has been removed.</p>
          <hr />
          <Button color="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Extract market and event data
  const { market, event, resolutions } = marketData;
  const disputeWindow = getDisputeWindowRemaining();

  return (
    <div className="page-content">
      <Breadcrumbs title="mrmto" breadcrumbItem="Resolution Detail" />

      <Row>
        {/* Market Info Card */}
        <Col xl={12}>
          <Card>
            <CardHeader className="d-flex align-items-center">
              <div className="me-3">
                {market?.market_image ? (
                  <img
                    src={market.market_image}
                    alt={market.name}
                    className="rounded"
                    width="64"
                    height="64"
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="rounded d-flex align-items-center justify-content-center bg-light text-secondary"
                    style={{ width: "64px", height: "64px" }}>
                    <i className="ri-image-line fs-24"></i>
                  </div>
                )}
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1">{event?.event_title}</h5>
                <p className="text-muted mb-0">{market?.name}</p>
              </div>
              <div>
                <Badge
                  color={getStatusBadgeColor(market?.resolution_state)}
                  className="fs-12 px-3 py-2">
                  {market?.resolution_state
                    ?.replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </Badge>
                {disputeWindow && !disputeWindow.expired && (
                  <p className="text-muted fs-12 mt-1 mb-0 text-end">
                    {disputeWindow.text}
                  </p>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={8}>
                  <Nav tabs className="nav-tabs-custom">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => toggleTab("1")}>
                        Market Details
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => toggleTab("2")}>
                        Event Details
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <TabContent
                    activeTab={activeTab}
                    className="p-3 border border-top-0 rounded-bottom">
                    {/* Market Details Tab */}
                    <TabPane tabId="1">
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <th width="200">Market Name</th>
                              <td>{market?.name}</td>
                            </tr>
                            <tr>
                              <th>Market Status</th>
                              <td>
                                <Badge
                                  color={getStatusBadgeColor(market?.status)}>
                                  {market?.status?.toUpperCase()}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <th>Resolution Status</th>
                              <td>
                                <Badge
                                  color={getStatusBadgeColor(
                                    market?.resolution_state,
                                  )}>
                                  {market?.resolution_state
                                    ?.replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <th>Start Date</th>
                              <td>{formatDateToLocal(market?.start_date)}</td>
                            </tr>
                            <tr>
                              <th>End Date</th>
                              <td>{formatDateToLocal(market?.end_date)}</td>
                            </tr>
                            <tr>
                              <th>Result</th>
                              <td>
                                {market?.result ? (
                                  <Badge
                                    color="success"
                                    className="text-uppercase">
                                    {market.result}
                                  </Badge>
                                ) : (
                                  <span className="text-muted">
                                    Not finalized
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Dispute Count</th>
                              <td>{market?.dispute_count || 0}</td>
                            </tr>
                            <tr>
                              <th>Bond Amount</th>
                              <td>${market?.bond_amount || 0}</td>
                            </tr>
                            <tr>
                              <th>Resolution Window</th>
                              <td>{market?.resolution_window || 0} hours</td>
                            </tr>
                            <tr>
                              <th>Resolution Window End</th>
                              <td>
                                {formatDateToLocal(
                                  marketData?.resolution_window_end,
                                )}
                                {disputeWindow && !disputeWindow.expired && (
                                  <span className="ms-2 text-muted">
                                    ({disputeWindow.text})
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Can Close Early</th>
                              <td>{market?.can_close_early ? "Yes" : "No"}</td>
                            </tr>
                            <tr>
                              <th>Settlement Time</th>
                              <td>{market?.settlement_time || 0} hours</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </TabPane>

                    {/* Event Details Tab */}
                    <TabPane tabId="2">
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <th width="200">Event Title</th>
                              <td>{event?.event_title}</td>
                            </tr>
                            <tr>
                              <th>Event Image</th>
                              <td>
                                {event?.event_image ? (
                                  <a
                                    href={event.event_image}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    <img
                                      src={event.event_image}
                                      alt={event.event_title}
                                      className="img-thumbnail"
                                      style={{ maxHeight: "100px" }}
                                    />
                                  </a>
                                ) : (
                                  <span className="text-muted">
                                    No image available
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>List Date</th>
                              <td>{formatDateToLocal(event?.list_date)}</td>
                            </tr>
                            <tr>
                              <th>Rules Summary</th>
                              <td>
                                {event?.rules_summary ||
                                  "No rules summary available"}
                              </td>
                            </tr>
                            <tr>
                              <th>Rules Document</th>
                              <td>
                                {event?.full_rules_doc_url ? (
                                  <a
                                    href={event.full_rules_doc_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-soft-primary">
                                    <i className="ri-file-text-line me-1"></i>
                                    View Rules Document
                                  </a>
                                ) : (
                                  <span className="text-muted">
                                    No rules document available
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Settlement Sources</th>
                              <td>
                                {event?.settlement_sources?.length > 0 ? (
                                  <ul className="mb-0 ps-3">
                                    {event.settlement_sources.map(
                                      (source, idx) => (
                                        <li key={idx}>{source}</li>
                                      ),
                                    )}
                                  </ul>
                                ) : (
                                  <span className="text-muted">
                                    No settlement sources specified
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Is Live Sports</th>
                              <td>{event?.is_live_sports ? "Yes" : "No"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </TabPane>
                  </TabContent>
                </Col>

                {/* Latest Resolution Info */}
                <Col md={4}>
                  <Card className="border h-100 mb-0">
                    <CardHeader className="bg-light">
                      <h5 className="mb-0 fs-15">Current Resolution Status</h5>
                    </CardHeader>
                    <CardBody>
                      {resolutions && resolutions.length > 0 ? (
                        <>
                          <div className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                              <Badge
                                color={getStatusBadgeColor(
                                  resolutions[0].status,
                                )}
                                className="me-2">
                                {resolutions[0].status.toUpperCase()}
                              </Badge>
                              <span className="text-muted fs-12">
                                {formatDateToLocal(resolutions[0].createdAt)}
                              </span>
                            </div>
                            <div>
                              <h5 className="mb-1">
                                Proposed Result:{" "}
                                <span className="text-primary">
                                  {resolutions[0].proposed_result}
                                </span>
                              </h5>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                              <div className="avatar-xs me-2">
                                {resolutions[0].user_id?.profile_image ? (
                                  <img
                                    src={resolutions[0].user_id.profile_image}
                                    alt={resolutions[0].user_id.username}
                                    className="rounded-circle img-fluid"
                                  />
                                ) : (
                                  <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                    {resolutions[0].user_id?.username
                                      ?.charAt(0)
                                      .toUpperCase() || "U"}
                                  </span>
                                )}
                              </div>
                              <div>
                                <h6 className="mb-0">
                                  {resolutions[0].user_id?.username || "Admin"}
                                </h6>
                                <small className="text-muted">
                                  {resolutions[0].user_id?.email || ""}
                                </small>
                              </div>
                            </div>
                          </div>

                          {(resolutions[0].evidence_url_1 ||
                            resolutions[0].evidence_url_2) && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">Evidence</h6>
                              {resolutions[0].evidence_url_1 && (
                                <a
                                  href={resolutions[0].evidence_url_1}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-soft-primary mb-2 w-100">
                                  <i className="ri-external-link-line me-1"></i>
                                  {
                                    new URL(resolutions[0].evidence_url_1)
                                      .hostname
                                  }
                                </a>
                              )}
                              {resolutions[0].evidence_url_2 && (
                                <a
                                  href={resolutions[0].evidence_url_2}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-soft-primary mb-2 w-100">
                                  <i className="ri-external-link-line me-1"></i>
                                  {
                                    new URL(resolutions[0].evidence_url_2)
                                      .hostname
                                  }
                                </a>
                              )}
                              {resolutions[0].evidence_description && (
                                <div className="p-2 bg-light rounded">
                                  <small className="text-muted">
                                    {resolutions[0].evidence_description}
                                  </small>
                                </div>
                              )}
                            </div>
                          )}

                          {resolutions[0].admin_id ? (
                            <div className="mb-0">
                              <h6 className="text-muted mb-2">Admin Review</h6>
                              <div className="d-flex justify-content-between mb-1">
                                <span className="fw-medium">Reviewed by:</span>
                                <span>{resolutions[0].admin_id.email}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span className="fw-medium">Review time:</span>
                                <span>
                                  {formatDateToLocal(
                                    resolutions[0].reviewed_at,
                                  )}
                                </span>
                              </div>
                              <div className="mt-2">
                                <span className="fw-medium">Review notes:</span>
                                <p className="mb-0 mt-1 p-2 bg-light rounded small">
                                  {resolutions[0].review_notes ||
                                    "No notes provided"}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4">
                              <div className="d-flex gap-2">
                                <Button
                                  color="success"
                                  className="w-100"
                                  onClick={() => {
                                    setActionType("approved");
                                    setReviewNotes("");
                                    setModalOpen(true);
                                  }}>
                                  <i className="ri-check-line me-1"></i> Approve
                                </Button>
                                <Button
                                  color="danger"
                                  className="w-100"
                                  onClick={() => {
                                    setActionType("rejected");
                                    setReviewNotes("");
                                    setModalOpen(true);
                                  }}>
                                  <i className="ri-close-line me-1"></i> Reject
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <i className="ri-file-search-line fs-40 text-muted"></i>
                          <p className="mt-2 mb-0">
                            No resolution information available
                          </p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* Resolution Timeline */}
        <Col xl={12} className="mt-4">
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">Resolution Timeline</h5>
            </CardHeader>
            <CardBody className="pt-4">
              {resolutions && resolutions.length > 0 ? (
                <div className="vertical-timeline">
                  {resolutions.map((resolution, index) => (
                    <div className="timeline-item" key={resolution._id}>
                      <div className="timeline-status">
                        <div
                          className={`timeline-badge bg-${getStatusBadgeColor(
                            resolution.status,
                          )}`}>
                          {resolution.resolution_type === "proposal" ? (
                            <i className="ri-file-list-line"></i>
                          ) : resolution.resolution_type === "dispute" ? (
                            <i className="ri-error-warning-line"></i>
                          ) : (
                            <i className="ri-check-line"></i>
                          )}
                        </div>
                      </div>

                      <div className="timeline-content">
                        <Card className="mb-0 border">
                          <CardHeader
                            className={`bg-light-subtle border-bottom d-flex justify-content-between align-items-center`}>
                            <div>
                              <h5 className="mb-0 fs-14">
                                {resolution.resolution_type === "proposal"
                                  ? "Resolution Proposal"
                                  : resolution.resolution_type === "dispute"
                                    ? `Dispute (Round ${resolution.dispute_round})`
                                    : "Resolution Update"}
                              </h5>
                              <small className="text-muted">
                                {formatDateToLocal(resolution.createdAt)}
                              </small>
                            </div>
                            <Badge
                              color={getStatusBadgeColor(resolution.status)}>
                              {resolution.status.toUpperCase()}
                            </Badge>
                          </CardHeader>
                          <CardBody className="p-3">
                            <div className="mb-3">
                              <div className="d-flex align-items-center mb-2">
                                <div className="avatar-xs me-2">
                                  {resolution.user_id?.profile_image ? (
                                    <img
                                      src={resolution.user_id.profile_image}
                                      alt={resolution.user_id.username}
                                      className="rounded-circle img-fluid"
                                    />
                                  ) : (
                                    <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                      {resolution.user_id?.username
                                        ?.charAt(0)
                                        .toUpperCase() || "U"}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <h6 className="mb-0">
                                    {resolution.user_id?.username || "Admin"}
                                  </h6>
                                  <small className="text-muted">
                                    {resolution.user_id?.email || ""}
                                  </small>
                                </div>
                              </div>
                            </div>

                            <Row className="mb-3">
                              <Col sm={6}>
                                <h6 className="mb-1 text-muted fs-12">
                                  Proposed Result
                                </h6>
                                <h5 className="mb-0">
                                  <Badge
                                    color={
                                      resolution.proposed_result === "yes"
                                        ? "success"
                                        : resolution.proposed_result === "no"
                                          ? "danger"
                                          : "info"
                                    }
                                    className="fs-12">
                                    {resolution.proposed_result?.toUpperCase()}
                                  </Badge>
                                </h5>
                              </Col>
                              <Col sm={6}>
                                <h6 className="mb-1 text-muted fs-12">
                                  Bond Amount
                                </h6>
                                <h5 className="mb-0">
                                  ${resolution.bond_amount}
                                </h5>
                                <small className="text-muted">
                                  {resolution.bond_returned
                                    ? "Bond returned"
                                    : "Bond not returned"}
                                </small>
                              </Col>
                            </Row>

                            {(resolution.evidence_url_1 ||
                              resolution.evidence_url_2) && (
                              <div className="mb-3">
                                <h6 className="text-muted mb-2">Evidence</h6>
                                {resolution.evidence_url_1 && (
                                  <a
                                    href={resolution.evidence_url_1}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-soft-primary mb-2 me-2">
                                    <i className="ri-external-link-line me-1"></i>
                                    {
                                      new URL(resolution.evidence_url_1)
                                        .hostname
                                    }
                                  </a>
                                )}
                                {resolution.evidence_url_2 && (
                                  <a
                                    href={resolution.evidence_url_2}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-soft-primary mb-2">
                                    <i className="ri-external-link-line me-1"></i>
                                    {
                                      new URL(resolution.evidence_url_2)
                                        .hostname
                                    }
                                  </a>
                                )}
                                {resolution.evidence_description && (
                                  <div className="p-2 bg-light rounded">
                                    <small>
                                      {resolution.evidence_description}
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}

                            {resolution.admin_id && (
                              <div className="admin-review mt-3 border-top pt-3">
                                <h6 className="text-muted mb-2">
                                  Admin Review
                                </h6>
                                <div className="d-flex justify-content-between mb-1">
                                  <span className="fw-medium">
                                    Reviewed by:
                                  </span>
                                  <span>{resolution.admin_id.email}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="fw-medium">
                                    Reviewed on:
                                  </span>
                                  <span>
                                    {formatDateToLocal(resolution.reviewed_at)}
                                  </span>
                                </div>
                                <div className="mt-2">
                                  <span className="fw-medium">
                                    Review notes:
                                  </span>
                                  <p className="mb-0 mt-1 p-2 bg-light rounded small">
                                    {resolution.review_notes ||
                                      "No notes provided"}
                                  </p>
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="ri-history-line fs-40 text-muted"></i>
                  <p className="mt-2 mb-0">No resolution history available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Bottom spacing to prevent content from hiding behind footer */}
      <div className="pb-5 mb-5"></div>

      {/* Review Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {actionType === "approved"
            ? "Approve Resolution"
            : "Reject Resolution"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="reviewNotes">Review Notes</Label>
              <Input
                type="textarea"
                id="reviewNotes"
                rows="5"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Enter optional notes about your decision..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalOpen(!modalOpen)}>
            Cancel
          </Button>
          <Button
            color={actionType === "approved" ? "success" : "danger"}
            onClick={() =>
              handleReviewSubmit(resolutions[0]._id, actionType, reviewNotes)
            }
            disabled={actionLoading}>
            {actionLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : actionType === "approved" ? (
              "Confirm Approval"
            ) : (
              "Confirm Rejection"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Alert Modal */}
      <Modal isOpen={alertModal} toggle={() => setAlertModal(!alertModal)}>
        <ModalHeader
          toggle={() => setAlertModal(!alertModal)}
          className={`bg-${alertType}`}>
          <span className="text-white">{alertTitle}</span>
        </ModalHeader>
        <ModalBody>{alertMessage}</ModalBody>
        <ModalFooter>
          <Button
            color={alertType}
            onClick={() => {
              setAlertModal(false);
              if (alertType === "success") {
                // Reload the page only if it was a successful operation
                window.location.reload();
              }
            }}>
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ResolutionDetail;
